'use client';

/** INBOX — global or project-scoped mail from beast-mailer (same /api/email/*).
 *  Folders w/ counts, business badges, message slab w/ flick ↑↓. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Glass, Badge, EmptyState, mono, display } from '@/future/primitives';

const NON_FACTORY_KEYS = ['tls', 'olga', 'anna', 'astroforyou', 'vctronics', 'aviationprivee', 'charters'];
const FOLDERS = ['primary', 'updates', 'social', 'promotions', 'forums', 'spam'];
const BADGE_HUES = ['#ffb454', '#7db4ff', '#b49aff', '#ff9ad5', '#5df2b4', '#e6c88a', '#f2a0b5', '#9ef0ff'];
const hue = (k: string) => BADGE_HUES[[...k].reduce((s, c) => s + c.charCodeAt(0), 0) % BADGE_HUES.length];

type Msg = { id: string; project: string | null; from_name: string; from_addr: string; subject: string; snippet?: string; body_text?: string; date: string; unread?: number; folder?: string };

export function InboxSurface({ scope, scopeName, onCompose }: { scope: string[] | 'factory' | null; scopeName?: string; onCompose?: () => void }) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [folder, setFolder] = useState('primary');
  const [open, setOpen] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [projCounts, setProjCounts] = useState<{ project: string; n: number }[]>([]);
  const [stats, setStats] = useState<any>(null);

  const filterKeys = useMemo(() => {
    if (Array.isArray(scope)) return scope;
    if (scope === 'factory') return projCounts.map(p => p.project).filter(k => k && !NON_FACTORY_KEYS.includes(k));
    return null;
  }, [scope, projCounts]);

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const [pc, inbox, st] = await Promise.all([
          fetch('/api/email/inbox/projects').then(r => r.json()).catch(() => []),
          fetch(`/api/email/inbox?limit=200&folder=${folder}${filterKeys?.length ? `&project=${filterKeys.join(',')}` : ''}`).then(r => r.json()),
          fetch('/api/email/stats').then(r => r.json()).catch(() => null),
        ]);
        if (!live) return;
        setProjCounts(Array.isArray(pc) ? pc : []);
        setMsgs(Array.isArray(inbox) ? inbox : []);
        setStats(st);
        setLoading(false);
      } catch { setLoading(false); }
    };
    load();
    const i = setInterval(load, 60_000);
    return () => { live = false; clearInterval(i); };
  }, [folder, filterKeys?.join(',')]);

  /* flick ↑↓ through the stack while a message is open */
  const nav = useCallback((d: number) => setOpen(o => o == null ? null : Math.max(0, Math.min(msgs.length - 1, o + d))), [msgs.length]);
  useEffect(() => {
    if (open == null) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); nav(1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); nav(-1); }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [open, nav]);

  const m = open != null ? msgs[open] : null;

  return (
    <div className="max-w-[1100px] mx-auto">
      {/* v4 GLOBAL INBOX room title */}
      <div style={{ textAlign: 'center', marginTop: 2 }}>
        <div style={{ fontWeight: 200, fontSize: 56, letterSpacing: '.14em', color: '#e9f9ff', textShadow: '0 0 18px rgba(0,240,255,.25)' }}>
          {scopeName ? `${scopeName.toUpperCase()} MAIL` : 'GLOBAL INBOX'}
        </div>
        <div style={{ ...mono(17, '.3em'), color: '#56788c', marginTop: 8 }}>
          {scopeName ? 'PROJECT STREAM · FULLY LOCAL' : 'ALL BUSINESSES · ONE STREAM · FULLY LOCAL'}
        </div>
      </div>
      {/* folder pills — v4 spec (active = cyan gradient pill) */}
      <div className="flex justify-center gap-[10px] flex-wrap" style={{ marginTop: 22 }}>
        {FOLDERS.map(f => (
          <button key={f} onMouseUp={() => setFolder(f)}
            style={folder === f
              ? { padding: '6px 14px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 16px rgba(0,240,255,.4)', ...mono(16, '.18em') }
              : { padding: '6px 14px', borderRadius: 999, color: '#56788c', border: '1px solid rgba(125,232,255,.16)', ...mono(16, '.18em') }}>
            {f.toUpperCase()}
          </button>
        ))}
      </div>
      {/* floating mail rows — v4 spec: 56px individual glass rows, badge right */}
      <div className="uif-scanlines" style={{ marginTop: 24 }}>
        {msgs.map((msg, i) => (
          <button key={msg.id} onMouseUp={() => setOpen(i)} className="w-full text-left" style={{ marginTop: i === 0 ? 0 : 10, display: 'block' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, height: 56, boxSizing: 'border-box', padding: '0 18px', borderRadius: 10, background: 'linear-gradient(170deg,rgba(40,140,180,.06),rgba(8,18,28,.42) 60%)', border: '1px solid rgba(125,232,255,.16)', transition: 'border-color .25s' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', background: msg.unread ? '#9ef0ff' : 'transparent', boxShadow: msg.unread ? '0 0 8px rgba(0,240,255,.8)' : 'none', border: msg.unread ? 'none' : '1px solid rgba(125,232,255,.2)' }} />
              <span style={{ width: 190, fontWeight: 400, fontSize: 25, color: msg.unread ? '#e9f9ff' : '#9fb8c6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 'none' }}>{msg.from_name || msg.from_addr}</span>
              <span style={{ flex: 1, fontWeight: 300, fontSize: 23, color: '#9fb8c6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{msg.subject}</span>
              <Badge label={msg.project || 'personal'} color={msg.project ? hue(msg.project) : '#56788c'} />
              <span style={{ ...mono(18), color: '#49677a', flex: 'none' }}>{new Date(msg.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            </div>
          </button>
        ))}
        {!loading && msgs.length === 0 && (
          <EmptyState what={`${scopeName || ''} mail`} action={scope ? "THIS PROJECT'S MAILBOX ISN'T CONNECTED TO BEAST" : 'NO MAIL IN THIS FOLDER'} />
        )}
        {loading && <div className="py-12 text-center" style={{ ...mono(17, '.3em'), color: 'var(--uif-t5)' }}>SYNCING MAIL…</div>}
      </div>
      {/* stats row — v4 spec: centered below the stream */}
      {stats && (
        <div className="flex justify-center gap-[26px] flex-wrap" style={{ ...mono(17, '.16em'), color: '#56788c', marginTop: 26, paddingBottom: 12 }}>
          <span>CONTACTS <span style={{ color: '#bfeaff' }}>{stats.contacts ?? 0}</span></span>
          <span>SENT <span style={{ color: '#bfeaff' }}>{stats.sent ?? 0}</span></span>
          <span>OPENED <span style={{ color: '#bfeaff' }}>{stats.opened ?? 0}</span></span>
          <span>CLICKED <span style={{ color: '#bfeaff' }}>{stats.clicked ?? 0}</span></span>
          <span>SCHEDULED <span style={{ color: '#bfeaff' }}>{stats.scheduled ?? 0}</span></span>
          <span>FAILED <span style={{ color: stats.failed ? '#ff7d8a' : '#bfeaff' }}>{stats.failed ?? 0}</span></span>
        </div>
      )}

      {/* message slab */}
      {m && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ background: 'var(--uif-backdrop)', backdropFilter: 'blur(6px)' }}
          onMouseUp={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div className="w-[760px] max-h-[78vh] overflow-y-auto rounded-2xl p-8" style={{
            background: 'var(--uif-slab-bg)', border: '1px solid var(--uif-slab-border)', boxShadow: 'var(--uif-slab-shadow)',
            animation: 'uifSlabIn .4s cubic-bezier(.25,.9,.3,1)',
          }}>
            <div className="h-[2px] w-full mb-5" style={{ background: `linear-gradient(90deg, ${m.project ? hue(m.project) : '#56788c'}, transparent 70%)` }} />
            <div className="flex items-center gap-3 mb-2">
              <Badge label={m.project || 'personal'} color={m.project ? hue(m.project) : '#56788c'} />
              <span style={{ ...mono(15), color: 'var(--uif-t5)' }}>{new Date(m.date).toLocaleString()}</span>
              <span className="ml-auto" style={{ ...mono(15, '.2em'), color: 'var(--uif-t6)' }}>↑↓ FLICK STACK · ESC CLOSE</span>
            </div>
            <h2 style={{ ...display(48, 200), color: 'var(--uif-t1)' }}>{m.subject}</h2>
            <div className="mt-1 mb-5" style={{ ...mono(17, '.1em'), color: 'var(--uif-t4)' }}>{m.from_name} · {m.from_addr}</div>
            <div style={{ ...display(27, 300), lineHeight: 1.75, color: 'var(--uif-t-body)', whiteSpace: 'pre-wrap' }}>
              {(m.body_text || m.snippet || '').slice(0, 4000)}
            </div>
            {/* reply-with-quote + follow-up — classic parity; prefill hands off to COMPOSE */}
            <div className="mt-6 flex gap-3 justify-end">
              <button onMouseUp={(e) => {
                e.stopPropagation();
                sessionStorage.setItem('uif:compose:prefill', JSON.stringify({
                  project: m.project || '', to: m.from_addr || '',
                  subject: `Re: ${m.subject}`,
                  body: `\n\n---\n${(m.body_text || m.snippet || '').split('\n').slice(0, 10).map(l => `> ${l}`).join('\n')}`,
                }));
                setOpen(null); onCompose?.();
              }} style={{ ...mono(15, '.16em'), color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 14px rgba(0,240,255,.35)', borderRadius: 999, padding: '7px 16px' }}>REPLY</button>
              <button onMouseUp={(e) => {
                e.stopPropagation();
                sessionStorage.setItem('uif:compose:prefill', JSON.stringify({
                  project: m.project || '', to: m.from_addr || '',
                  subject: `Follow-up: ${(m.subject || '').replace(/^(Re|Fwd|Follow-up): */i, '')}`, body: '',
                }));
                setOpen(null); onCompose?.();
              }} style={{ ...mono(15, '.16em'), color: '#c9b8ff', border: '1px solid rgba(180,154,255,.4)', background: 'rgba(180,154,255,.08)', borderRadius: 999, padding: '7px 16px' }}>FOLLOW-UP</button>
            </div>
          </div>
        </div>
      )}
      <style jsx global>{`@keyframes uifSlabIn { from { transform: scale(.85) translateY(40px); opacity: 0 } to { transform: none; opacity: 1 } }`}</style>
    </div>
  );
}
